import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FormProvider,
  getValidationResolver,
  Inscription,
  Preloader,
  useForm,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { UNAUTHORIZED_STATUS_CODE } from '@/api/api';
import { VALIDATORS } from '@/common';
import { AUTH_ERROR_KEY } from '@/common/constants';
import { ModalWrapper } from '@/components/wrappers';
import useChangePasswordQuery from '@/modules/user/components/ManageProfile/components/Security/queries/useChangePassword.query';
import {
  ChangePasswordField,
  ChangePasswordForm,
  ChangePasswordStep,
} from '@/modules/user/components/ManageProfile/components/Security/security.types';
import theme from '@/styles/theme';

import ChangePasswordErrorModal from './ChangePasswordErrorModal';
import EnterNewPasswordFields from './EnterNewPasswordFields';

interface Props {
  onSetVisible: (value: boolean) => void;
  setStep: (step: ChangePasswordStep) => void;
}

const EnterNewPasswordModal: React.FC<Props> = ({ onSetVisible, setStep }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { changePassword, isLoading } = useChangePasswordQuery();

  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const methods = useForm<ChangePasswordForm>({
    resolver: getValidationResolver({
      [ChangePasswordField.existingPassword]: yup.string().required(AUTH_ERROR_KEY.PASSWORD_IS_REQUIRED),
      [ChangePasswordField.newPassword]: VALIDATORS.PASSWORD,
      [ChangePasswordField.confirmNewPassword]: yup
        .string()
        .required(AUTH_ERROR_KEY.PASSWORD_IS_REQUIRED)
        .oneOf([yup.ref(ChangePasswordField.newPassword)], AUTH_ERROR_KEY.PASSWORD_DO_NOT_MATCH),
    }),
  });

  const handleCancel = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleSubmit = useCallback(
    async (passwords: ChangePasswordForm) => {
      try {
        await changePassword({
          password: passwords[ChangePasswordField.existingPassword],
          newPassword: passwords[ChangePasswordField.newPassword],
        });
        setStep(ChangePasswordStep.SUCCESSFULLY_CHANGED);
      } catch (error) {
        const errorCode = _.get(error, 'response.status', 0) as number;
        const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

        const isCurrentPasswordInvalid = errorCode === UNAUTHORIZED_STATUS_CODE;
        const isNewPasswordTheSame = errorMessage?.includes(AUTH_ERROR_KEY.PASSWORD_MUST_BE_DIFFERENT);

        if (isCurrentPasswordInvalid) {
          methods.setError(ChangePasswordField.existingPassword, {
            message: AUTH_ERROR_KEY.PASSWORD_INVALID,
          });
          setIsErrorModalVisible(false);
          return;
        }

        if (isNewPasswordTheSame) {
          methods.setError(ChangePasswordField.newPassword, {
            message: AUTH_ERROR_KEY.PASSWORD_MUST_BE_DIFFERENT,
          });
          methods.setError(ChangePasswordField.confirmNewPassword, {
            message: AUTH_ERROR_KEY.PASSWORD_MUST_BE_DIFFERENT,
          });
          setIsErrorModalVisible(false);
          return;
        }

        setIsErrorModalVisible(true);
        throw new Error();
      }
    },
    [changePassword, setStep, methods]
  );

  if (isErrorModalVisible) {
    return (
      <ChangePasswordErrorModal
        onSetVisible={setIsErrorModalVisible}
        onSubmit={methods.handleSubmit(handleSubmit)}
      />
    );
  }

  return (
    <ModalWrapper
      testId="change-password"
      visible
      onSetVisible={onSetVisible}
      title={t('Change Password')}
      titleBoxStyle={{ paddingBottom: theme.spacings.spacing8 }}
    >
      <Inscription size={theme.spacings.m} textAlign="center">
        {t('Please enter an existing password and choose your new password.')}
      </Inscription>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        background="module"
        pad={isMobile ? 'spacing8' : 'spacing24'}
        round="moduleRound"
      >
        <FormProvider {...methods}>
          <form>
            <EnterNewPasswordFields editMode />
          </form>
        </FormProvider>
      </Box>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        direction={isMobile ? 'column-reverse' : 'row'}
        justify="end"
        gap="spacing12"
      >
        <AppButton width={isMobile ? '100%' : '140px'} buttonType="secondary" onClick={handleCancel}>
          {t('Cancel')}
        </AppButton>

        <AppButton
          width={isMobile ? '100%' : '140px'}
          disabled={isLoading}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          {isLoading ? <Preloader color="white" /> : t('Confirm')}
        </AppButton>
      </Box>
    </ModalWrapper>
  );
};

export default EnterNewPasswordModal;
